/**
 * The symbols of the IPA Keyboard, grouped the way the International Phonetic
 * Association's chart groups them (IPA Chart, revised to 2020, © International Phonetic
 * Association, CC BY-SA 3.0, https://www.internationalphoneticassociation.org/content/ipa-chart).
 *
 * Every entry is the exact Unicode text the button inserts. Several look like ordinary
 * Latin or Greek letters and are not: the IPA "g" is U+0261 (ɡ, "latin small letter script
 * g"), not the ASCII g; the voiced bilabial fricative is the Greek beta U+03B2; the
 * dental fricatives are U+03B8 (θ) and U+00F0 (ð). Diacritics that sit on another letter
 * are Unicode combining marks; the modifier letters (ʰ ʷ ʲ ˠ ˤ ⁿ ˡ ˞) are ordinary
 * spacing characters. The test file pins the code points.
 */

export const PLACES = [
  "bilabial",
  "labiodental",
  "dental",
  "alveolar",
  "postalveolar",
  "retroflex",
  "palatal",
  "velar",
  "uvular",
  "pharyngeal",
  "glottal",
] as const;
export type Place = (typeof PLACES)[number];

export const MANNERS = [
  "plosive",
  "nasal",
  "trill",
  "tap",
  "fricative",
  "lateralFricative",
  "approximant",
  "lateralApproximant",
] as const;
export type Manner = (typeof MANNERS)[number];

/** A cell of the consonant table: [voiceless, voiced]; "" where the chart has none. */
export type Pair = readonly [string, string];

/** Pulmonic consonants, by manner (row) and place (column). Empty cells are omitted. */
export const PULMONIC: Readonly<Record<Manner, Partial<Record<Place, Pair>>>> =
  {
    plosive: {
      bilabial: ["p", "b"],
      alveolar: ["t", "d"],
      retroflex: ["\u0288", "\u0256"],
      palatal: ["c", "\u025F"],
      velar: ["k", "\u0261"],
      uvular: ["q", "\u0262"],
      glottal: ["\u0294", ""],
    },
    nasal: {
      bilabial: ["", "m"],
      labiodental: ["", "\u0271"],
      alveolar: ["", "n"],
      retroflex: ["", "\u0273"],
      palatal: ["", "\u0272"],
      velar: ["", "\u014B"],
      uvular: ["", "\u0274"],
    },
    trill: {
      bilabial: ["", "\u0299"],
      alveolar: ["", "r"],
      uvular: ["", "\u0280"],
    },
    tap: {
      labiodental: ["", "\u2C71"],
      alveolar: ["", "\u027E"],
      retroflex: ["", "\u027D"],
    },
    fricative: {
      bilabial: ["\u0278", "\u03B2"],
      labiodental: ["f", "v"],
      dental: ["\u03B8", "\u00F0"],
      alveolar: ["s", "z"],
      postalveolar: ["\u0283", "\u0292"],
      retroflex: ["\u0282", "\u0290"],
      palatal: ["\u00E7", "\u029D"],
      velar: ["x", "\u0263"],
      uvular: ["\u03C7", "\u0281"],
      pharyngeal: ["\u0127", "\u0295"],
      glottal: ["h", "\u0266"],
    },
    lateralFricative: {
      alveolar: ["\u026C", "\u026E"],
    },
    approximant: {
      labiodental: ["", "\u028B"],
      alveolar: ["", "\u0279"],
      retroflex: ["", "\u027B"],
      palatal: ["", "j"],
      velar: ["", "\u0270"],
    },
    lateralApproximant: {
      alveolar: ["", "l"],
      retroflex: ["", "\u026D"],
      palatal: ["", "\u028E"],
      velar: ["", "\u029F"],
    },
  };

/** A symbol with a stable key for its name in the widget's strings. */
export interface ChartSymbol {
  id: string;
  /** The exact text inserted. */
  text: string;
}

/** Non-pulmonic consonants: clicks, voiced implosives and ejectives. */
export const CLICKS: readonly ChartSymbol[] = [
  { id: "clickBilabial", text: "\u0298" },
  { id: "clickDental", text: "\u01C0" },
  { id: "clickAlveolar", text: "\u01C3" },
  { id: "clickPalatoalveolar", text: "\u01C2" },
  { id: "clickLateral", text: "\u01C1" },
];

export const IMPLOSIVES: readonly ChartSymbol[] = [
  { id: "implosiveBilabial", text: "\u0253" },
  { id: "implosiveDental", text: "\u0257" },
  { id: "implosivePalatal", text: "\u0284" },
  { id: "implosiveVelar", text: "\u0260" },
  { id: "implosiveUvular", text: "\u029B" },
];

export const EJECTIVE: ChartSymbol = { id: "ejective", text: "\u02BC" };

/** "Other symbols" on the chart, plus the tie bars that join an affricate. */
export const OTHER_SYMBOLS: readonly ChartSymbol[] = [
  { id: "labialVelarFricative", text: "\u028D" },
  { id: "labialVelarApproximant", text: "w" },
  { id: "labialPalatalApproximant", text: "\u0265" },
  { id: "epiglottalFricative", text: "\u029C" },
  { id: "epiglottalVoicedFricative", text: "\u02A2" },
  { id: "epiglottalPlosive", text: "\u02A1" },
  { id: "alveoloPalatalFricative", text: "\u0255" },
  { id: "alveoloPalatalVoicedFricative", text: "\u0291" },
  { id: "alveolarLateralFlap", text: "\u027A" },
  { id: "simultaneousPostalveolarVelar", text: "\u0267" },
];

/** Combining tie bars: above the pair, or below it. */
export const TIE_BARS: readonly ChartSymbol[] = [
  { id: "tieAbove", text: "\u0361" },
  { id: "tieBelow", text: "\u035C" },
];

/** Letters common in dictionaries and teaching materials that the chart itself omits. */
export const EXTRA_LETTERS: readonly ChartSymbol[] = [
  { id: "rColouredSchwa", text: "\u025A" },
  { id: "rColouredOpenMid", text: "\u025D" },
  { id: "velarizedL", text: "\u026B" },
];

export const HEIGHTS = [
  "close",
  "nearClose",
  "closeMid",
  "mid",
  "openMid",
  "nearOpen",
  "open",
] as const;
export type Height = (typeof HEIGHTS)[number];

export const BACKNESS = [
  "front",
  "nearFront",
  "central",
  "nearBack",
  "back",
] as const;
export type Backness = (typeof BACKNESS)[number];

/** Vowels by height (row) and backness (column): [unrounded, rounded]. */
export const VOWELS: Readonly<Record<Height, Partial<Record<Backness, Pair>>>> =
  {
    close: {
      front: ["i", "y"],
      central: ["\u0268", "\u0289"],
      back: ["\u026F", "u"],
    },
    nearClose: {
      nearFront: ["\u026A", "\u028F"],
      nearBack: ["", "\u028A"],
    },
    closeMid: {
      front: ["e", "\u00F8"],
      central: ["\u0258", "\u0275"],
      back: ["\u0264", "o"],
    },
    mid: {
      central: ["\u0259", ""],
    },
    openMid: {
      front: ["\u025B", "\u0153"],
      central: ["\u025C", "\u025E"],
      back: ["\u028C", "\u0254"],
    },
    nearOpen: {
      front: ["\u00E6", ""],
      central: ["\u0250", ""],
    },
    open: {
      front: ["a", "\u0276"],
      back: ["\u0251", "\u0252"],
    },
  };

/** Diacritics. `combining` marks attach to the character before them. */
export interface Diacritic extends ChartSymbol {
  combining: boolean;
}

const RE_MARK_START = /^\p{M}/u;

/** Is this text a Unicode combining mark, which attaches to the character before it? */
export function isCombining(text: string): boolean {
  return RE_MARK_START.test(text);
}

function mark(id: string, text: string): Diacritic {
  return { id, text, combining: isCombining(text) };
}

export const DIACRITICS: readonly Diacritic[] = [
  mark("voiceless", "\u0325"),
  mark("voicelessAbove", "\u030A"),
  mark("voiced", "\u032C"),
  mark("aspirated", "\u02B0"),
  mark("moreRounded", "\u0339"),
  mark("lessRounded", "\u031C"),
  mark("advanced", "\u031F"),
  mark("retracted", "\u0320"),
  mark("centralized", "\u0308"),
  mark("midCentralized", "\u033D"),
  mark("syllabic", "\u0329"),
  mark("syllabicAbove", "\u030D"),
  mark("nonSyllabic", "\u032F"),
  mark("nonSyllabicAbove", "\u0311"),
  mark("rhoticity", "\u02DE"),
  mark("breathy", "\u0324"),
  mark("creaky", "\u0330"),
  mark("linguolabial", "\u033C"),
  mark("labialized", "\u02B7"),
  mark("palatalized", "\u02B2"),
  mark("velarized", "\u02E0"),
  mark("pharyngealized", "\u02E4"),
  mark("velarizedOrPharyngealized", "\u0334"),
  mark("raised", "\u031D"),
  mark("lowered", "\u031E"),
  mark("advancedTongueRoot", "\u0318"),
  mark("retractedTongueRoot", "\u0319"),
  mark("dental", "\u032A"),
  mark("apical", "\u033A"),
  mark("laminal", "\u033B"),
  mark("nasalized", "\u0303"),
  mark("nasalRelease", "\u207F"),
  mark("lateralRelease", "\u02E1"),
  mark("noAudibleRelease", "\u031A"),
];

export const SUPRASEGMENTALS: readonly Diacritic[] = [
  mark("primaryStress", "\u02C8"),
  mark("secondaryStress", "\u02CC"),
  mark("long", "\u02D0"),
  mark("halfLong", "\u02D1"),
  mark("extraShort", "\u0306"),
  mark("minorGroup", "|"),
  mark("majorGroup", "\u2016"),
  mark("syllableBreak", "."),
  mark("linking", "\u203F"),
];

export const TONE_ACCENTS: readonly Diacritic[] = [
  mark("accentExtraHigh", "\u030B"),
  mark("accentHigh", "\u0301"),
  mark("accentMid", "\u0304"),
  mark("accentLow", "\u0300"),
  mark("accentExtraLow", "\u030F"),
  mark("accentRising", "\u030C"),
  mark("accentFalling", "\u0302"),
  mark("accentHighRising", "\u1DC4"),
  mark("accentLowRising", "\u1DC5"),
  mark("accentRisingFalling", "\u1DC8"),
  mark("downstep", "\uA71C"),
  mark("upstep", "\uA71B"),
  mark("globalRise", "\u2197"),
  mark("globalFall", "\u2198"),
];

export const TONE_LETTERS: readonly ChartSymbol[] = [
  { id: "toneExtraHigh", text: "\u02E5" },
  { id: "toneHigh", text: "\u02E6" },
  { id: "toneMid", text: "\u02E7" },
  { id: "toneLow", text: "\u02E8" },
  { id: "toneExtraLow", text: "\u02E9" },
];

/** The dotted circle used to show a combining mark on its own. */
export const DOTTED_CIRCLE = "\u25CC";

/** `U+0283` for each code point of a text, in order. */
export function codePointLabels(text: string): string[] {
  return Array.from(text, (char) => {
    const code = char.codePointAt(0) ?? 0;
    return `U+${code.toString(16).toUpperCase().padStart(4, "0")}`;
  });
}

/* ------------------------------------------------------------------ editing */

export interface Edit {
  text: string;
  /** Where the caret goes afterwards. */
  caret: number;
}

const RE_SPACE_OR_EMPTY = /^\s?$/;

/**
 * Insert a symbol into a text at the selection. A plain symbol replaces the selection.
 * A combining mark is added after the selection instead — it belongs to the character
 * before it — and is refused (null) when there is no character to attach it to, so a
 * mark never ends up alone or after a space.
 */
export function insertSymbol(
  text: string,
  start: number,
  end: number,
  symbol: string,
  combining: boolean
): Edit | null {
  if (!combining) {
    return {
      text: text.slice(0, start) + symbol + text.slice(end),
      caret: start + symbol.length,
    };
  }
  const before = text.slice(Math.max(0, end - 1), end);
  if (RE_SPACE_OR_EMPTY.test(before)) {
    return null;
  }
  return {
    text: text.slice(0, end) + symbol + text.slice(end),
    caret: end + symbol.length,
  };
}

/** Backspace: remove the selection, or the code point before the caret. */
export function deleteBefore(text: string, start: number, end: number): Edit {
  if (end > start) {
    return { text: text.slice(0, start) + text.slice(end), caret: start };
  }
  if (start === 0) {
    return { text, caret: 0 };
  }
  const code = text.codePointAt(start - 1) ?? 0;
  const lowSurrogate = code >= 0xdc_00 && code <= 0xdf_ff;
  const cut = lowSurrogate && start >= 2 ? start - 2 : start - 1;
  return { text: text.slice(0, cut) + text.slice(start), caret: cut };
}
